import React, { useState } from "react";
import Message from "../../../../i18n/Message";
import "./ExamplesSection.scss";
import {
  ChevronLeftIcon,
  MessageSquareTextIcon,
  PlusIcon,
  Trash2Icon,
} from "lucide-react";
import ExampleMessageFeed from "./ExampleMessageFeed";
import { useUiMode } from "../context/UiModeContext";
import { useManage, useReadOnly } from "../context/ManageContext";
import UpgradeHint from "../../../shared/subscription/UpgradeHint";
import backend from "../../../../backend/backend";
import { loadMcpPlaygrounds } from "../../store/mcpPlaygroundActionCreators";
import { useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import Notify from "../../../shared/Notify";
import label from "../../../../i18n/label";
import GenerateExampleDialog from "./GenerateExampleDialog";

const ExamplesSection = ({ playground }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [selectedIdx, setSelectedIdx] = useState(null);
  const { uiMode } = useUiMode();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const manage = useManage();
  const readOnly = useReadOnly();

  const onDeleteConfirmed = () => {
    const examples = JSON.parse(playground.demoChatJson);
    examples.splice(selectedIdx, 1);
    const data = {
      ...playground,
      demoChatJson: JSON.stringify(examples),
    };
    backend
      .savePlayground(data)
      .then((resp) => {
        console.log("Saved playground: ", resp);
        dispatch(loadMcpPlaygrounds(intl));
        setSelectedIdx(0);
        setShowDeleteConfirm(false);
      })
      .catch((err) => {
        console.log("Failed to save playground: ", err);
        Notify.error(label(intl, "error_unexpected"));
      });
  };

  const onGenerateExample = () => {
    setShowGenerateDialog(true);
  };

  const onExampleGenerated = (generatedExample) => {
    const examples = playground.demoChatJson
      ? JSON.parse(playground.demoChatJson)
      : [];

    examples.unshift(generatedExample);

    const data = {
      ...playground,
      demoChatJson: JSON.stringify(examples),
    };

    backend
      .savePlayground(data)
      .then((resp) => {
        console.log("Saved playground with new example: ", resp);
        dispatch(loadMcpPlaygrounds(intl));
        setShowGenerateDialog(false);
      })
      .catch((err) => {
        console.log("Failed to save playground with new example: ", err);
        Notify.error(label(intl, "error_unexpected"));
      });
  };

  if (!playground.demoChatJson) {
    return (
      <div>
        <div>No examples added yet.</div>
      </div>
    );
  }

  const examples = JSON.parse(playground.demoChatJson);

  console.log("Got examples: ", examples);

  if (selectedIdx === null) {
    return (
      <div className={`examples_section ${uiMode.toLowerCase()}_mode`}>
        <div className="example_section_title_container">
          <h2 className="examples_section_title">
            <Message id="widget_examples_title" />
          </h2>
          {manage && (
            <div className="examples_generate_action">
              <button
                className="btn no-btn"
                disabled={readOnly}
                onClick={() => !readOnly && onGenerateExample()}
              >
                <PlusIcon size={16} />
                <Message id="generate" />
              </button>
              {readOnly && (
                <UpgradeHint messageId="upgrade_hint_readonly" plan={undefined} />
              )}
            </div>
          )}
        </div>
        {examples.length > 0 && (
          <div className="examples_help">
            <Message id="widget_examples_help" />
          </div>
        )}
        <div className="example_list">
          {examples.map((example, index) => {
            return (
              <div
                className="example_item"
                onClick={() => setSelectedIdx(index)}
              >
                <MessageSquareTextIcon size={20} />
                {example.summary}
              </div>
            );
          })}
        </div>
        {showGenerateDialog && (
          <GenerateExampleDialog
            onClose={() => setShowGenerateDialog(false)}
            playground={playground}
            onFinished={onExampleGenerated}
          />
        )}
      </div>
    );
  } else {
    const example = examples[selectedIdx];
    return (
      <div className={`example_chat_section ${uiMode.toLowerCase()}_mode`}>
        <div className="breadcrumbs" onClick={() => setSelectedIdx(null)}>
          <ChevronLeftIcon size={16} />
          <Message id="widget_examples_back" />
        </div>
        <div className="example_title_container">
          <h2 className="example_chat_title">{example.summary}</h2>
          {manage && (
            <div className="example_chat_toolbar">
              <div className="confirm_container">
                {showDeleteConfirm && (
                  <>
                    <div
                      className="cover"
                      onClick={() => setShowDeleteConfirm(false)}
                    ></div>
                    <div className="confirm_panel">
                      <button
                        className="btn bg-gradient-primary btn-sm"
                        onClick={onDeleteConfirmed}
                      >
                        <Message id="confirm_delete" />
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                className="btn no-btn"
                disabled={readOnly}
                onClick={(e) => {
                  e.stopPropagation();
                  if (readOnly) return;
                  setShowDeleteConfirm(!showDeleteConfirm);
                }}
              >
                <Trash2Icon size={16} />
                <Message id="delete" />
              </button>
              {readOnly && (
                <UpgradeHint messageId="upgrade_hint_readonly" plan={undefined} />
              )}
            </div>
          )}
        </div>

        <ExampleMessageFeed messages={example.messages} />
        {showGenerateDialog && (
          <GenerateExampleDialog
            onClose={() => setShowGenerateDialog(false)}
            playground={playground}
            onFinished={onExampleGenerated}
          />
        )}
      </div>
    );
  }
};

export default ExamplesSection;
